
import { defineComponent } from "vue";
import type { Component } from "vue";
import {
  BAO_CAO_VIEWER_PER_PAGE_OPTIONS,
  BAO_CAO_VIEWER_DEFAULT_PER_PAGE,
  getBaoCaoPdfLayoutOptions,
  getBaoCaoReportTitle,
  parseBaoCaoPdfParams,
  resolveBaoCaoViewerPerPage,
} from "@/core/helpers/bao-cao-pdf-params";
import { swalErrNotification } from "@/core/helpers/utils";
import PdfViewerPage from "@/components/shared/pdf-viewer/PdfViewerPage.vue";
import QuantityInputCustom from "@/components/forms-elements/QuantityInputCustom.vue";
import { generateHtmlElementPdf } from "@/components/shared/pdf-viewer/generate-html-element-pdf";
import {
  getPdfContentWidthPx,
  getPdfPrintContentStyle,
} from "@/components/shared/pdf-viewer/pdf-content.config";
import {
  loadBaoCaoViewerData,
  type BaoCaoViewerPagination,
} from "./load-bao-cao-viewer-data";

export default defineComponent({
  name: "BaoCaoViewer",

  components: { PdfViewerPage, QuantityInputCustom },

  data() {
    return {
      templateComponent: null as Component | null,
      templateProps: {} as Record<string, unknown>,
      pdfSource: null as string | null,
      isGenerating: false,
      loadingMessage: "Đang tải dữ liệu...",
      pagination: null as BaoCaoViewerPagination | null,
      pageInput: 1,
      perPageInput: BAO_CAO_VIEWER_DEFAULT_PER_PAGE,
      perPageOptions: [...BAO_CAO_VIEWER_PER_PAGE_OPTIONS],
    };
  },

  computed: {
    pageTitle(): string {
      const reportType = String(this.$route.params.reportType ?? "");
      const sheetRaw = this.$route.query.sheet;
      const sheet = Array.isArray(sheetRaw) ? sheetRaw[0] : sheetRaw;
      return getBaoCaoReportTitle(reportType, sheet ?? undefined);
    },

    pdfFileName(): string {
      const reportType = String(this.$route.params.reportType ?? "bao-cao");
      const sheetRaw = this.$route.query.sheet;
      const sheet = Array.isArray(sheetRaw) ? sheetRaw[0] : sheetRaw;
      return sheet ? `${reportType}-${sheet}.pdf` : `${reportType}.pdf`;
    },

    pdfLayoutOptions() {
      return getBaoCaoPdfLayoutOptions(
        String(this.$route.params.reportType ?? "")
      );
    },

    printPaperStyle(): Record<string, string> {
      return getPdfPrintContentStyle(this.pdfLayoutOptions);
    },

    hiddenTemplateStyle(): Record<string, string> {
      return { width: `${getPdfContentWidthPx(this.pdfLayoutOptions)}px` };
    },
  },

  methods: {
    waitForTemplateRender(): Promise<void> {
      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => resolve());
        });
      });
    },

    goToPage(page: number) {
      if (this.isGenerating) return;

      const target = Math.max(
        1,
        Math.min(page, this.pagination?.lastPage ?? page)
      );

      if (target === this.pagination?.currentPage) {
        this.pageInput = target;
        return;
      }

      this.$router.replace({
        query: { ...this.$route.query, page: String(target) },
      });
    },

    onPageInputChange(value: number) {
      const target = Math.round(value || 1);
      this.goToPage(target);
    },

    onPerPageChange(perPage: number) {
      if (this.isGenerating) return;

      const resolvedPerPage = resolveBaoCaoViewerPerPage(perPage);
      const currentPerPage = resolveBaoCaoViewerPerPage(
        Number(this.$route.query.perPage) || this.pagination?.perPage
      );

      if (resolvedPerPage === currentPerPage) {
        this.perPageInput = resolvedPerPage;
        return;
      }

      const nextQuery = {
        ...this.$route.query,
        perPage: String(resolvedPerPage),
      };
      delete nextQuery.page;

      this.$router.replace({ query: nextQuery });
    },

    async loadAndGenerate() {
      const params = parseBaoCaoPdfParams(
        String(this.$route.params.reportType ?? ""),
        this.$route.query as Record<string, string | string[] | undefined>
      );

      if (!params) {
        swalErrNotification(null, "Loại báo cáo không hợp lệ");
        return;
      }

      this.perPageInput = resolveBaoCaoViewerPerPage(params.perPage);

      try {
        this.isGenerating = true;
        this.pdfSource = null;
        this.loadingMessage = "Đang tải dữ liệu...";

        const viewerState = await loadBaoCaoViewerData(params, {
          onLoadProgress: (loadedPage, totalPages) => {
            this.loadingMessage = `Đang tải dữ liệu (trang ${loadedPage}/${totalPages})...`;
          },
        });
        this.templateComponent = viewerState.template;
        this.templateProps = viewerState.templateProps;
        this.pagination = viewerState.pagination ?? null;

        if (this.pagination) {
          this.pageInput = this.pagination.currentPage;
          this.perPageInput = this.pagination.perPage;
        }

        this.loadingMessage = "Đang tạo file PDF...";

        await this.$nextTick();
        await this.waitForTemplateRender();

        await this.generatePdf();
      } catch (e) {
        swalErrNotification(e, "Có lỗi khi tải báo cáo");
      } finally {
        this.isGenerating = false;
      }
    },

    async generatePdf() {
      const el = (this.$refs.printArea as HTMLElement)
        ?.firstElementChild as HTMLElement | null;
      if (!el) {
        throw new Error("Không tìm thấy nội dung báo cáo");
      }

      this.pdfSource = await generateHtmlElementPdf(
        el,
        this.pdfLayoutOptions,
        (current, total) => {
          if (total > 1) {
            this.loadingMessage = `Đang tạo file PDF (${current}/${total})...`;
          }
        }
      );
    },
  },

  watch: {
    "$route.query.page"(newPage, oldPage) {
      if (newPage === oldPage) return;
      this.loadAndGenerate();
    },
    "$route.query.perPage"(newPerPage, oldPerPage) {
      if (newPerPage === oldPerPage) return;
      this.loadAndGenerate();
    },
  },

  mounted() {
    this.loadAndGenerate();
  },
});
